import { stringify } from 'qs';

import { IExpenseFormFields } from '@Shared/expense_form_fields';
import { ExpenseMetadata } from '@Shared/interfaces/ExpenseMetadata';
import { ICompanyCategoryModel, IExpenseTagBucketModel } from '@Shared/models';

import { Api as apiService } from '../api';

export interface ICurrency {
  code: string;
  decimal: number;
  name: string;
  symbol?: string;
}

export interface IVendor {
  abacus_category?: string;
  category_id?: string;
  icon: string;
  name: string;
  source: string;
  subtitle: string;
  venue_id?: string;
  venue_name?: string;
}

export interface ILocationOption {
  name: string;
  label: string;
  place_id: string;
}

export interface ILocationDetails extends Partial<ILocationOption> {
  lat: string;
  lng: string;
  formatted_address: string;
}

interface IExchangeRate {
  decimal: number;
  from: string;
  rate: number;
  to: string;
}

export abstract class FormFieldsApi {
  static get(
    amount: number,
    categoryId: string,
    expenseSource:
      | 'corporate_card'
      | 'manual_international_workaround'
      | 'corporate_card_international_workaround'
      | 'manual'
      | 'personal_card'
      | 'manual_international_workaround'
      | 'personal_card_international_workaround'
      | 'reimbursable',
    expenseTagIds: string[],
    userProfileId: string,
    vendor: string,
    linkedAccountCardType: string
  ) {
    const params: Record<string, any> = {
      amount: amount,
      category_id: categoryId,
      source: expenseSource,
      expense_tag_ids: expenseTagIds,
      user_profile_id: userProfileId,
      vendor: vendor,
      linked_account_card_type: linkedAccountCardType
    };

    let url = '/api/web/expenses/form_fields?';

    for (const key of Object.keys(params)) {
      const value = params[key];
      if (typeof value === 'object') {
        for (const id of value) {
          url += `${key}=${id}&`;
        }
      } else {
        url += `${key}=${value}&`;
      }
    }

    return apiService.get(url).then((response) => response.json() as Promise<IExpenseFormFields>);
  }

  static getSelectableBucketsAndCategories(
    selectedTags?: string[],
    userId?: string
  ): Promise<{ categories: ICompanyCategoryModel[]; expenseTagBuckets: IExpenseTagBucketModel[] }> {
    let query = '';

    if (selectedTags?.length) {
      selectedTags.forEach((tag) => {
        query += `&selectedTags[]=${tag}`;
      });
    }

    if (userId) {
      query += `&userId=${userId}`;
    }

    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/form_fields/buckets_categories?${query}`));
  }

  static getCurrencies(): Promise<ICurrency[]> {
    return apiService.responseToJSONAndToastError(apiService.get('/api/web/metadata/currencies'));
  }

  static getExchangeRate(date: string, currency: string, base: string): Promise<IExchangeRate> {
    const query = `date=${date}&currency=${currency}&base=${base}`;

    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/metadata/exchange_rate?${query}`));
  }

  static searchVendors(searchTerm: string, userId: string, nearCity?: string, latLon?: string): Promise<IVendor[]> {
    let query = `query=${searchTerm}&user_id=${userId}`;

    if (nearCity) {
      query += `&near=${nearCity}`;
    }

    if (latLon) {
      query += `&ll=${latLon}`;
    }

    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/vendors/search?${query}`));
  }

  static getSearchPlaces(input: string): Promise<ILocationOption[]> {
    let lat;
    let lng;
    navigator.geolocation.getCurrentPosition(function (position) {
      lat = position.coords.latitude;
      lng = position.coords.longitude;
    });

    const searchText = encodeURIComponent(input);
    let query = `query=${searchText}&lat=${lat}&lng=${lng}`;

    const places = apiService.get(`/api/web/organization/integrations/googlemaps/places?${query}`);

    return apiService.responseToJSONAndToastError(places);
  }

  static getPlaceDetails(placeId: string): Promise<ILocationDetails> {
    let query = `place_id=${placeId}`;

    const placeDetails = apiService.get(`/api/web/organization/integrations/googlemaps/placeDetails?${query}`);

    return apiService.responseToJSONAndToastError(placeDetails);
  }

  static getMapUrl(metadata: ExpenseMetadata) {
    const query = stringify({
      origin_lat: metadata.mileage_origin_lat,
      origin_lng: metadata.mileage_origin_lng,
      destination_lat: metadata.mileage_destination_lat,
      destination_lng: metadata.mileage_destination_lng,
      round_trip: metadata.mileage_round_trip
    });

    const data = apiService.get(`/api/web/organization/integrations/googlemaps/directions?${query}`);

    return apiService.responseToJSONAndToastError(data);
  }
}
